/**
 * Safe path parameter validation for API hooks.
 * Prevents SSRF by validating user-supplied values before interpolating into URLs.
 */

export const SAFE_PATH_PARAM_REGEX = /^[a-zA-Z0-9_-]+$/;

/** Predefined validators for common param types (e.g. email contains @ and .) */
export const PATH_PARAM_VALIDATORS: Record<string, RegExp> = {
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  /**
   * S3 object key (may include /): allow typical file names (spaces, parens, unicode, etc.).
   * Rejects .. (traversal), newlines, and ? / # which are awkward in URL paths before encoding.
   * Values are still passed through encodeURIComponent when building the request path.
   */
  s3_key: /^(?!.*\.\.)[^?#\r\n]+$/,
};

export interface ValidatePathParamsResult {
  valid: boolean;
  path: string | null;
}

/**
 * Validates path params against a template and returns the constructed path or null.
 * Template uses :paramName placeholders (e.g. /organizations/:name).
 * Default validator: alphanumeric + underscore + hyphen.
 * Custom validators can be passed for params like email.
 */
export function validatePathParams(
  template: string,
  pathParams: Record<string, string | number | undefined>,
  customValidators?: Record<string, RegExp>
): ValidatePathParamsResult {
  const paramNames = template.match(/:([a-zA-Z_][a-zA-Z0-9_]*)/g);
  if (!paramNames || paramNames.length === 0) {
    return { valid: true, path: template };
  }

  let path = template;
  const validators = { ...PATH_PARAM_VALIDATORS, ...customValidators };

  for (const placeholder of paramNames) {
    const paramName = placeholder.slice(1); // Remove leading :
    const rawValue = pathParams[paramName];
    const value =
      rawValue !== undefined && rawValue !== null
        ? String(rawValue).trim()
        : '';

    const validator = validators[paramName] ?? SAFE_PATH_PARAM_REGEX;
    if (!validator.test(value)) {
      return { valid: false, path: null };
    }

    path = path.replaceAll(placeholder, encodeURIComponent(value));
  }

  return { valid: true, path };
}
